import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import axios from '../../utils/axios';
import { MP_LABEL, parseForcedValue } from './msAssortmentApi.mjs';

function cardKey(card) {
  return `${card.marketplace}:${card.account_id}:${card.product_key}`;
}

function linkKind(card) {
  const linked = card?.linked_to;
  if (!linked) return 'new';
  if (linked.same_ms) return 'same';
  if (linked.archived || linked.missing) return 'rebind_dead';
  return 'rebind_live';
}

function linkedName(linked) {
  return linked?.name || linked?.ms_id || '—';
}

function forcedHint(forced) {
  const list = (Array.isArray(forced) ? forced : [])
    .filter((f) => f && f.forced_stock != null && String(f.forced_stock) !== '');
  if (!list.length) return '';
  return `будет перенесён: ${list.map((f) => `${f.binding_name || f.binding_id}: ${f.forced_stock}`).join(', ')}`;
}

function pendingFromCard(card) {
  const kind = linkKind(card);
  return {
    key: cardKey(card),
    card,
    kind,
    enabled: kind === 'new' ? false : !!card.linked_to?.enabled,
    forced: '',
  };
}

function compactParams(params) {
  const out = {};
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === '') continue;
    out[key] = value;
  }
  return out;
}

function CardsTable({ cards, pendingKeys, onAdd, onRebindLive, emptyText }) {
  return (
    <TableContainer sx={{ maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>МП</TableCell>
            <TableCell>Кабинет</TableCell>
            <TableCell>Карточка</TableCell>
            <TableCell>SKU</TableCell>
            <TableCell>ШК</TableCell>
            <TableCell align="right">Привязка</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cards.map((card) => {
            const key = cardKey(card);
            const kind = linkKind(card);
            const inPending = pendingKeys.has(key);
            const linked = card.linked_to;
            return (
              <TableRow key={key} hover>
                <TableCell>
                  <Chip size="small" label={MP_LABEL[card.marketplace] || card.marketplace} />
                </TableCell>
                <TableCell>{card.account_name || card.account_id}</TableCell>
                <TableCell>
                  <Typography variant="body2">{card.article || card.product_key}</Typography>
                  {card.name && (
                    <Typography variant="caption" color="text.secondary" display="block">{card.name}</Typography>
                  )}
                  {(card.barcode_match || card.archived) && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      {card.barcode_match && <Chip size="small" color="success" variant="outlined" label="ШК совпал" />}
                      {card.archived && <Chip size="small" color="default" variant="outlined" label="архив на МП" />}
                    </Stack>
                  )}
                </TableCell>
                <TableCell>{card.sku || '—'}</TableCell>
                <TableCell>{(card.barcodes || []).join(' / ') || '—'}</TableCell>
                <TableCell align="right">
                  {kind === 'same' && (
                    <Typography variant="body2" color="text.secondary">Уже привязана к этому товару</Typography>
                  )}
                  {kind === 'new' && (
                    <Button size="small" onClick={() => onAdd(card)} disabled={inPending}>
                      {inPending ? 'В списке' : 'Добавить'}
                    </Button>
                  )}
                  {kind === 'rebind_dead' && (
                    <Stack spacing={0.5} alignItems="flex-end">
                      <Typography variant="body2" color="text.secondary">
                        {linked.missing && !linked.archived
                          ? `Привязана к удалённому товару${linked.name ? `: ${linked.name}` : ''}`
                          : `Привязана к архивному: ${linkedName(linked)}`}
                      </Typography>
                      <Button size="small" onClick={() => onAdd(card)} disabled={inPending}>
                        {inPending ? 'В списке' : 'Перепривязать'}
                      </Button>
                    </Stack>
                  )}
                  {kind === 'rebind_live' && (
                    <Stack spacing={0.5} alignItems="flex-end">
                      <Typography variant="body2" color="warning.main">
                        Привязана к: {linkedName(linked)}
                      </Typography>
                      <Button size="small" color="warning" onClick={() => onRebindLive(card)} disabled={inPending}>
                        {inPending ? 'В списке' : 'Перепривязать…'}
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {!cards.length && (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function MsLinkDialog({ open, row, onClose, onSaved, notify, prefill }) {
  const [tab, setTab] = useState('barcode');
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [mp, setMp] = useState('');
  const [accountId, setAccountId] = useState('');
  const [q, setQ] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  const [pending, setPending] = useState([]);
  const [confirmCard, setConfirmCard] = useState(null);
  const [saving, setSaving] = useState(false);
  const sessionRef = useRef(0);

  const msId = row?.ms_id;
  const msName = row?.name || info?.ms?.name || msId;

  const addPending = useCallback((card) => {
    setPending((prev) => {
      const key = cardKey(card);
      if (prev.some((item) => item.key === key)) return prev;
      return [...prev, pendingFromCard(card)];
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const session = ++sessionRef.current;
    setTab(prefill?.marketplace && !prefill?.product_key ? 'search' : 'barcode');
    setInfo(null);
    setLoadError('');
    setMp(prefill?.marketplace && !prefill?.product_key ? prefill.marketplace : '');
    setAccountId(prefill?.account_id != null && !prefill?.product_key ? String(prefill.account_id) : '');
    setQ('');
    setIncludeArchived(false);
    setSearchResults([]);
    setSearched(false);
    setSearching(false);
    setPending([]);
    setConfirmCard(null);
    setSaving(false);
    if (!msId) return undefined;

    setLoading(true);
    axios.get('/ms-assortment/link-candidates', { params: { ms_id: msId, q: '', include_archived: 0 }, silent: true })
      .then(({ data }) => {
        if (sessionRef.current !== session) return;
        setInfo(data || null);
      })
      .catch((error) => {
        if (sessionRef.current !== session) return;
        const message = error?.response?.data?.message || 'Не удалось загрузить карточки';
        setLoadError(message);
        notify.error(message);
      })
      .finally(() => {
        if (sessionRef.current === session) setLoading(false);
      });

    if (prefill?.product_key) {
      axios.get('/ms-assortment/link-candidates', {
        params: compactParams({
          ms_id: msId,
          q: prefill.product_key,
          marketplace: prefill.marketplace,
          account_id: prefill.account_id,
          include_archived: 1,
        }),
        silent: true,
      })
        .then(({ data }) => {
          if (sessionRef.current !== session) return;
          const cards = [...(data?.search || []), ...(data?.by_barcode || [])];
          const found = cards.find((card) => String(card.product_key) === String(prefill.product_key)
            && (!prefill.marketplace || card.marketplace === prefill.marketplace)
            && (prefill.account_id == null || prefill.account_id === '' || String(card.account_id) === String(prefill.account_id)));
          if (!found) {
            notify.warning(`Карточка ${prefill.product_key} не найдена`);
            return;
          }
          if (linkKind(found) === 'same') {
            notify.info('Карточка уже привязана к этому товару');
            return;
          }
          addPending(found);
        })
        .catch((error) => {
          if (sessionRef.current !== session) return;
          notify.error(error?.response?.data?.message || 'Не удалось найти карточку');
        });
    }
    return undefined;
  }, [open, msId, prefill?.marketplace, prefill?.account_id, prefill?.product_key, addPending, notify]);

  const accounts = useMemo(() => (Array.isArray(info?.accounts) ? info.accounts : []), [info]);
  const accountOptions = useMemo(
    () => accounts.filter((acc) => !mp || acc.marketplace === mp),
    [accounts, mp],
  );
  const msBarcodes = Array.isArray(info?.ms?.barcodes) ? info.ms.barcodes : (row?.barcode ? [row.barcode] : []);
  const byBarcode = Array.isArray(info?.by_barcode) ? info.by_barcode : [];
  const pendingKeys = useMemo(() => new Set(pending.map((item) => item.key)), [pending]);

  const changeMp = (value) => {
    setMp(value);
    if (value && accountId) {
      const acc = accounts.find((a) => String(a.id) === String(accountId));
      if (acc && acc.marketplace !== value) setAccountId('');
    }
  };

  const runSearch = async () => {
    if (!msId) return;
    const session = sessionRef.current;
    setSearching(true);
    try {
      const { data } = await axios.get('/ms-assortment/link-candidates', {
        params: compactParams({
          ms_id: msId,
          q: q.trim(),
          marketplace: mp,
          account_id: accountId,
          include_archived: includeArchived ? 1 : 0,
        }),
        silent: true,
      });
      if (sessionRef.current !== session) return;
      setSearchResults(Array.isArray(data?.search) ? data.search : []);
      setSearched(true);
      if (data?.accounts && !info?.accounts) setInfo((prev) => ({ ...(prev || {}), accounts: data.accounts }));
    } catch (error) {
      if (sessionRef.current !== session) return;
      notify.error(error?.response?.data?.message || 'Не удалось выполнить поиск');
    } finally {
      if (sessionRef.current === session) setSearching(false);
    }
  };

  const updatePending = (key, patch) => {
    setPending((prev) => prev.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  };

  const removePending = (key) => {
    setPending((prev) => prev.filter((item) => item.key !== key));
  };

  const confirmRebindLive = () => {
    if (confirmCard) addPending(confirmCard);
    setConfirmCard(null);
  };

  const save = async () => {
    if (!msId || !pending.length) return;
    const items = pending.map((item) => {
      const { card, kind } = item;
      const payload = {
        marketplace: card.marketplace,
        account_id: card.account_id,
        product_key: card.product_key,
        enabled: !!item.enabled,
      };
      const forced = parseForcedValue(item.forced);
      if (forced != null) payload.forced_stock = forced;
      if (kind === 'rebind_dead') payload.rebind_from = card.linked_to.ms_id;
      if (kind === 'rebind_live') {
        payload.rebind_from = card.linked_to.ms_id;
        payload.force = true;
      }
      return payload;
    });
    setSaving(true);
    try {
      const { data } = await axios.post(`/ms-assortment/products/${msId}/links/batch`, { items }, { silent: true, timeout: 60000 });
      notify.success(`Сохранено: ${data?.saved ?? items.length}`);
      onSaved?.();
      onClose();
    } catch (error) {
      const body = error?.response?.data;
      if (error?.response?.status === 409 && body?.code === 'card_already_linked') {
        const names = (body.conflicts || [])
          .map((c) => `${MP_LABEL[c.marketplace] || c.marketplace} ${c.product_key} → ${c.ms_name || c.ms_id}`)
          .join('; ');
        notify.error(`${body.message || 'Карточка уже привязана к другому товару'}${names ? `: ${names}` : ''}`);
      } else {
        notify.error(body?.message || 'Не удалось сохранить связи');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Привязать карточки МП
          <Typography variant="body2" color="text.secondary">
            {msName}{msBarcodes.length ? ` · ШК: ${msBarcodes.join(' / ')}` : ''}
            {info?.ms?.archived ? ' · архивный в МС' : ''}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
            <Tab value="barcode" label={`По ШК (${byBarcode.length})`} />
            <Tab value="search" label="Поиск" />
          </Tabs>
          {loading && <LinearProgress sx={{ mb: 1 }} />}
          {loadError && !loading && <Alert severity="error" sx={{ mb: 1 }}>{loadError}</Alert>}

          {tab === 'barcode' && !loading && !loadError && (
            msBarcodes.length === 0 && !byBarcode.length ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>У товара нет ШК</Typography>
            ) : (
              <CardsTable
                cards={byBarcode}
                pendingKeys={pendingKeys}
                onAdd={addPending}
                onRebindLive={setConfirmCard}
                emptyText="Карточек с таким ШК не найдено"
              />
            )
          )}

          {tab === 'search' && (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>МП</InputLabel>
                  <Select label="МП" value={mp} onChange={(e) => changeMp(e.target.value)}>
                    <MenuItem value="">Все</MenuItem>
                    <MenuItem value="wb">WB</MenuItem>
                    <MenuItem value="ozon">Ozon</MenuItem>
                    <MenuItem value="yandex">Yandex</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Кабинет</InputLabel>
                  <Select label="Кабинет" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                    <MenuItem value="">Все кабинеты</MenuItem>
                    {accountOptions.map((acc) => (
                      <MenuItem key={`${acc.marketplace}:${acc.id}`} value={String(acc.id)}>
                        {MP_LABEL[acc.marketplace] || acc.marketplace} · {acc.name || acc.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Поиск"
                  placeholder="offer_id / артикул / nmID / SKU / ШК / название"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      runSearch();
                    }
                  }}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="outlined"
                  startIcon={<SearchIcon />}
                  onClick={runSearch}
                  disabled={searching || !msId}
                >
                  Найти
                </Button>
              </Stack>
              <FormControlLabel
                control={<Switch checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />}
                label="Показывать архивные карточки"
              />
              {searching && <LinearProgress />}
              <CardsTable
                cards={searchResults}
                pendingKeys={pendingKeys}
                onAdd={addPending}
                onRebindLive={setConfirmCard}
                emptyText={searched ? 'Ничего не найдено' : 'Введите запрос и нажмите «Найти»'}
              />
            </Stack>
          )}

          <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>К сохранению ({pending.length})</Typography>
          {!pending.length ? (
            <Typography variant="body2" color="text.secondary">Добавьте карточки из списка выше</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>МП</TableCell>
                    <TableCell>Кабинет</TableCell>
                    <TableCell>Артикул / SKU</TableCell>
                    <TableCell>Тип</TableCell>
                    <TableCell>Включить трансляцию</TableCell>
                    <TableCell>Принуд</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.map((item) => {
                    const { card, kind } = item;
                    const linked = card.linked_to;
                    const isRebind = kind === 'rebind_dead' || kind === 'rebind_live';
                    const hint = isRebind && item.forced === '' ? forcedHint(linked?.forced) : '';
                    return (
                      <TableRow key={item.key}>
                        <TableCell>{MP_LABEL[card.marketplace] || card.marketplace}</TableCell>
                        <TableCell>{card.account_name || card.account_id}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{card.article || card.product_key}</Typography>
                          {card.sku && (
                            <Typography variant="caption" color="text.secondary">SKU {card.sku}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {kind === 'new' && 'новая'}
                          {kind === 'rebind_dead' && `перенос с архива: ${linkedName(linked)}`}
                          {kind === 'rebind_live' && (
                            <Typography variant="body2" color="warning.main">перенос с {linkedName(linked)}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                size="small"
                                checked={!!item.enabled}
                                onChange={(e) => updatePending(item.key, { enabled: e.target.checked })}
                              />
                            )}
                            label={isRebind ? (
                              <Typography variant="caption" color="text.secondary">
                                было {linked?.enabled ? 'вкл' : 'выкл'}
                              </Typography>
                            ) : ''}
                          />
                        </TableCell>
                        <TableCell sx={{ minWidth: 160 }}>
                          <TextField
                            size="small"
                            type="number"
                            placeholder="не задавать"
                            value={item.forced}
                            onChange={(e) => updatePending(item.key, { forced: e.target.value })}
                            helperText={hint || undefined}
                            fullWidth
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Убрать из списка">
                            <IconButton size="small" onClick={() => removePending(item.key)} disabled={saving}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {saving && <Box sx={{ mt: 1 }}><LinearProgress /></Box>}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={save} disabled={saving || !pending.length}>
            {saving ? 'Сохраняем…' : `Сохранить (${pending.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmCard} onClose={() => setConfirmCard(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Перепривязать карточку?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Карточка уже привязана к живому товару {linkedName(confirmCard?.linked_to)}.
            Перенести на {msName}? Старая связь будет снята.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCard(null)}>Отмена</Button>
          <Button variant="contained" color="warning" onClick={confirmRebindLive}>Перенести</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
